import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import { visuallyHidden } from '@mui/utils';
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import { alpha } from '@mui/material/styles';
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import MUITable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import MultiSelectFilter from "../Common/Filters/MultiSelectFilter";
import {useTranslation} from "../../context/Internationalization";

const isColumnHideable = (column) => {
    const hasTextLabel = typeof column.label === 'string' || typeof column.label === 'number';
    return column.hideable !== false && hasTextLabel && String(column.label).trim().length > 0;
};

const areColumnIdsEqual = (currentIds, nextIds) =>
    currentIds.length === nextIds.length && currentIds.every((id, index) => id === nextIds[index]);

function EnhancedTableHead(props) {
    const { columns, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, isSelectable, selectAllAriaLabel } = props;
    const createSortHandler = (property, isSortable = true) => (event) => {
        if(isSortable) {
            onRequestSort(event, property);
        }
    };

    return (
        <TableHead>
            <TableRow key={1}>
                { isSelectable ? <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': selectAllAriaLabel || 'select all rows',
                        }}
                    />
                </TableCell> : null}
                {columns.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={headCell.sx}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id, headCell.sortable)}
                            disabled={headCell.sortable === false}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const EnhancedTableToolbar = (props) => {
    const { numSelected } = props;

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
        >
            {numSelected > 0 ? (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} selected
                </Typography>
            ) : (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    Nutrition
                </Typography>
            )}
            {numSelected > 0 ? (
                <Tooltip title={t("delete")}>
                    <IconButton>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title={t("filter")}>
                    <IconButton>
                        <FilterListIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Toolbar>
    );
};

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function Table({rows, columns, rowElement, onSort, order, orderBy, isSelectable, onAllSelected = () => {}, resetSelections = false, selectionResetKey, selectAllAriaLabel, tableAriaLabel, tableSx, size = 'medium', disableAutoColumnSelection = false}) {

    const [selected, setSelected] = React.useState([]);
    const [allSelected, setAllSelected] = React.useState(false);
    const [hasHorizontalOverflow, setHasHorizontalOverflow] = React.useState(false);
    const tableContainerRef = React.useRef(null);
    const {t = (key) => key} = useTranslation();
    const columnOptions = React.useMemo(() => columns
        .filter(isColumnHideable)
        .map((column) => ({
            value: column.id,
            label: String(column.label),
        })), [columns]);
    const hideableColumnIds = React.useMemo(
        () => columnOptions.map((column) => column.value),
        [columnOptions]
    );
    const previousHideableColumnIds = React.useRef(hideableColumnIds);
    const [visibleColumnIds, setVisibleColumnIds] = React.useState(hideableColumnIds);
    const effectiveVisibleColumnIds = React.useMemo(
        () => visibleColumnIds.filter((columnId) => hideableColumnIds.includes(columnId)),
        [hideableColumnIds, visibleColumnIds]
    );
    const canSelectColumns = !disableAutoColumnSelection && columnOptions.length > 1;
    const hasHiddenColumns = effectiveVisibleColumnIds.length < hideableColumnIds.length;

    const checkHorizontalOverflow = React.useCallback(() => {
        const container = tableContainerRef.current;
        setHasHorizontalOverflow(Boolean(
            container && container.scrollWidth > container.clientWidth + 1
        ));
    }, []);

    const handleRequestSort = (event, property) => {
        if(onSort) {
            const isAsc = orderBy === property && order === 'asc';
            onSort({direction: isAsc ? 'desc' : 'asc', key: property})
        }
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = rows.map((n) => n._id);
            setSelected(newSelecteds);
            setAllSelected(true);
            return;
        }
        setSelected([]);
        setAllSelected(false);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const isSelected = (id) => selected.indexOf(id) !== -1;

    useEffect(() => {
        onAllSelected(allSelected);
    }, [allSelected]);

    useEffect(() => {
        if(resetSelections) {
            setSelected([]);
            setAllSelected(false);
        }
    }, [resetSelections]);

    useEffect(() => {
        if(selectionResetKey !== undefined) {
            setSelected([]);
            setAllSelected(false);
        }
    }, [selectionResetKey]);

    useEffect(() => {
        const previousColumnIds = previousHideableColumnIds.current;

        setVisibleColumnIds((currentVisibleColumnIds) => {
            const retainedColumnIds = currentVisibleColumnIds.filter((columnId) =>
                hideableColumnIds.includes(columnId)
            );
            const addedColumnIds = hideableColumnIds.filter((columnId) =>
                !previousColumnIds.includes(columnId)
            );
            const nextVisibleColumnIds = [...retainedColumnIds, ...addedColumnIds];

            if (!nextVisibleColumnIds.length && hideableColumnIds.length) {
                nextVisibleColumnIds.push(hideableColumnIds[0]);
            }

            return areColumnIdsEqual(currentVisibleColumnIds, nextVisibleColumnIds)
                ? currentVisibleColumnIds
                : nextVisibleColumnIds;
        });

        previousHideableColumnIds.current = hideableColumnIds;
    }, [hideableColumnIds]);

    useEffect(() => {
        if (!canSelectColumns) {
            setHasHorizontalOverflow(false);
            return undefined;
        }

        const container = tableContainerRef.current;
        if (!container) return undefined;

        checkHorizontalOverflow();
        window.addEventListener('resize', checkHorizontalOverflow);

        const resizeObserver = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(checkHorizontalOverflow);

        if (resizeObserver) {
            resizeObserver.observe(container);
            const tableElement = container.querySelector('table');
            if (tableElement) resizeObserver.observe(tableElement);
        }

        return () => {
            window.removeEventListener('resize', checkHorizontalOverflow);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [canSelectColumns, checkHorizontalOverflow, columns, rows, visibleColumnIds]);

    const hiddenColumnStyles = React.useMemo(() => {
        if (!canSelectColumns) return {};

        return columns.reduce((styles, column, columnIndex) => {
            if (isColumnHideable(column) && !effectiveVisibleColumnIds.includes(column.id)) {
                const cellIndex = columnIndex + 1 + (isSelectable ? 1 : 0);
                styles[`& > thead > tr > *:nth-child(${cellIndex})`] = {display: 'none'};
                styles[`& > tbody > tr > *:nth-child(${cellIndex})`] = {display: 'none'};
            }

            return styles;
        }, {});
    }, [canSelectColumns, columns, effectiveVisibleColumnIds, isSelectable]);

    const handleVisibleColumnsChange = ({target: {value}}) => {
        if (value.length) setVisibleColumnIds(value);
    };
        

    return (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
            {canSelectColumns && (hasHorizontalOverflow || hasHiddenColumns) ? (
                <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2.5}}>
                    <MultiSelectFilter
                        name="visibleTableColumns"
                        label={t('table_columns')}
                        options={columnOptions}
                        value={effectiveVisibleColumnIds}
                        onChange={handleVisibleColumnsChange}
                        icon={<ViewColumnOutlinedIcon fontSize="small" />}
                    />
                </Box>
            ) : null}
            <Paper sx={{ width: '100%', mb: 2 }}>
                {/*<EnhancedTableToolbar numSelected={selected.length} />*/}                 
                <TableContainer ref={tableContainerRef} sx={{ overflowX: 'auto' }}>
                    <MUITable
                        sx={{ width: '100%', ...hiddenColumnStyles, ...tableSx }}
                        aria-label={tableAriaLabel}
                        aria-labelledby={tableAriaLabel ? undefined : "tableTitle"}
                        size={size}
                    >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            columns={columns}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                            isSelectable={isSelectable}
                            selectAllAriaLabel={selectAllAriaLabel}
                        />
                        <TableBody>
                            {rows.map((row, index) => {
                                    const isItemSelected = isSelected(row._id);

                                    return (rowElement(row, index, isItemSelected, handleClick))
                                })
                            }

                        </TableBody>
                    </MUITable>
                </TableContainer>
            </Paper>
        </Box>
    );
}

Table.propTypes = {

};

export default Table;
